import React, { useEffect, useState } from 'react';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Form } from 'react-bootstrap';
import styled, {keyframes} from 'styled-components';
import { t } from 'ttag';
import { Modifier } from '../../../../../../../contexts/site/site-context';
import { money } from '../../../../../../../services/format';
import { Theme } from "../../../../../../../contexts/operator/operator-context";
import { rgba } from "polished";
import { Preferences } from "../../../../MenuPage";
import useOperatorContext from "../../../../../../../contexts/operator/useOperatorContext";

type ModifierCheckboxProps = {
    originalModifier: Modifier;
    type: 'quantity' | 'checkbox' | 'radio';
    selected: boolean;
    lastCheckbox: boolean;
    maxReached: boolean;
    updateModifier: (m: Modifier, quantity: number) => void;
    preferences: Preferences;
};

const ModifierCheckbox = ({ originalModifier, type, selected, lastCheckbox, maxReached, updateModifier, preferences }: ModifierCheckboxProps) => {
    const operatorContext = useOperatorContext();

    const [quantity, setQuantity] = useState<number>(originalModifier.quantity);
    const [showAnim, setShowAnim] = useState(false);

    useEffect(() => {
        setShowAnim(false);
        if (selected) {
            setTimeout(() => {
                setShowAnim(true);
            }, 500)
        }
    }, [selected])

    useEffect(() => {
        if ( type === 'radio' ) { return; }
        if ( quantity === null ) { return; }
        updateModifier(originalModifier, quantity ?? 0);
    }, [quantity]);

    useEffect(() => {
        setQuantity(originalModifier.quantity);
    }, [originalModifier]);

    const renderTitleBadges = () => {
        let badges = [];
        if (originalModifier.is_new) {
            badges.push(
                <Badge key={`tfbg-${originalModifier.uid}-new`} className="badge--title badge--title--new">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`New`}
                    </span>
                </Badge>,
            );
        }
        if (originalModifier.is_popular) {
            badges.push(
                <Badge key={`tfbg-${originalModifier.uid}-popular`} className="badge--title badge--title--popular">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`Popular`}
                    </span>
                </Badge>,
            );
        }
        if (originalModifier.is_offer) {
            badges.push(
                <Badge key={`tfbg-${originalModifier.uid}-offer`} className="badge--title badge--title--offer">
                    <div className="badge--title__icon material-symbols-outlined material-symbols-outlined--filled">
                        kid_star
                    </div>
                    <span className={'badge--title__text'}>
                        {t`Meal deal`}
                    </span>
                </Badge>,
            );
        }
        return badges.map((badge) => badge);
    };

    const renderDietBadges = () => {
        let badges = [];
        if (originalModifier.allergens) {
            badges.push(
                <OverlayTrigger
                    key={'allergenPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-allergenPlacement`}>
                            { originalModifier.allergens }
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular badge--modifier" key={`tfbg-${originalModifier.uid}-allergens`} title="Allergens">
                        A
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (originalModifier.is_vegan) {
            badges.push(
                <OverlayTrigger
                    key={'veganPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-veganPlacement`}>
                            Suitable for Vegans
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular badge--modifier" key={`tfbg-${originalModifier.uid}-vegan`} title="Vegan">
                        Vg
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (originalModifier.is_vegetarian) {
            badges.push(
                <OverlayTrigger
                    key={'vegetarianPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-vegetarianPlacement2`}>
                            Suitable for Vegetarians
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular badge--modifier" key={`tfbg-${originalModifier.uid}-vegetarian`} title="Vegetarian">
                        V
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (originalModifier.is_gluten_free) {
            badges.push(
                <OverlayTrigger
                    key={'gfPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-gfPlacement`}>
                            Gluten-Free
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular badge--modifier" key={`tfbg-${originalModifier.uid}-gf`} title="Gluten-Free">
                        Gf
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (originalModifier.is_halal) {
            badges.push(
                <OverlayTrigger
                    key={'hPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-hPlacement`}>
                            Halal
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular badge--modifier" key={`tfbg-${originalModifier.uid}-h`} title="Halal">
                        H
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (originalModifier.is_kosher) {
            badges.push(
                <OverlayTrigger
                    key={'kPlacement'}
                    placement={'top'}
                    overlay={
                        <Tooltip id={`tooltip-kPlacement`}>
                            Kosher
                        </Tooltip>
                    }
                >
                    <Badge className="badge--menu-item badge--menu-item--circular badge--modifier" key={`tfbg-${originalModifier.uid}-k`} title="Kosher">
                        K
                    </Badge>
                </OverlayTrigger>
            );
        }

        if (badges.length > 0) {
            return <>{badges.map((badge) => badge)}</>;
        }
        return null;
    };

    const handleRadioClick = () => {
        updateModifier(originalModifier, 1);
    };

    const handleQuantityClick = (value: number) => {
        if (maxReached && value > 0) {
            return;
        }

        setQuantity(quantity + value);
    };

    const handleCheckboxClick = () => {
        if (quantity === 0 && !maxReached) {
            setQuantity(1);
        } else {
            setQuantity(0);
        }

        updateModifier(originalModifier, quantity);
    };

    if (originalModifier.is_hidden) {
        return null;
    }

    return (
        <Layout lastCheckbox={lastCheckbox}>
            <StyledButton
                modifier={originalModifier}
                aria-label={'Item, Price'}
                tabIndex={0}
            >
                <ModifierWrapper as={ type === 'quantity' ? 'div' : 'label' }>
                    <div className={'mod-details'}>
                        <span className="mod-name">
                            <span className="mod-name-text">
                                { originalModifier.name }
                            </span>
                            { renderDietBadges() }
                            { renderTitleBadges() }
                        </span>
                        <span className="mod-description">
                            { originalModifier.description.length > 0 && originalModifier.description }
                            { preferences.calories && originalModifier.calories && originalModifier.calories.length > 0 ? (
                                <>
                                    { originalModifier.description.length > 0 && ' (' }
                                    { originalModifier.calories } kcal
                                    { originalModifier.description.length > 0 && ')' }
                                </>
                            ) : null }
                        </span>
                    </div>
                    <span className="mod-price">
                        {originalModifier.price ? ' +' + money(originalModifier.price, operatorContext.operator?.ccy, operatorContext.operator?.locale) : ''}
                    </span>
                    {type === 'radio' && (
                        <RadioLayout>
                            <StyledRadio>
                                <Form.Check type={type} id={`mod-${type}-${originalModifier.uid}`} onClick={handleRadioClick} />
                                <span className={`material-symbols-outlined ${selected ? 'anim--modifier-pulse' : ''}`}>{ selected ? 'radio_button_checked' : 'radio_button_unchecked' }</span>
                            </StyledRadio>
                        </RadioLayout>
                    )}
                    {type === 'quantity' && (
                        <QuantityWrapper>
                            { quantity > 0 && (
                                <>
                                    { quantity > 1 ? (
                                        <button onClick={() => handleQuantityClick(-1)} type="button" aria-label="Decrease Quantity">
                                            <span className="material-symbols-outlined">do_not_disturb_on</span>
                                        </button>
                                    ) : (
                                        <button onClick={() => handleQuantityClick(-1)} type="button" aria-label="Decrease Quantity">
                                            <span className="material-symbols-outlined">delete</span>
                                        </button>
                                    )}
                                    <span>{quantity}</span>
                                </>
                            )}
                            <button onClick={() => handleQuantityClick(1)} type="button" aria-label="Increase Quantity">
                                <span className={`material-symbols-outlined ${maxReached ? 'material-symbols-outlined--disabled' : ''}`}>add_circle</span>
                            </button>
                        </QuantityWrapper>
                    )}
                    {type === 'checkbox' && (
                        <CheckboxLayout>
                            <StyledCheckbox>
                                <Form.Check type={type} id={`mod-${type}-${originalModifier.uid}`} onClick={handleCheckboxClick} />
                                <span className={`material-symbols-outlined ${!selected && maxReached ? 'material-symbols-outlined--disabled' : ''} ${selected ? 'material-symbols-outlined--filled anim--modifier-pulse' : ''}`}>{ selected ? 'check_box' : 'check_box_outline_blank' }</span>
                            </StyledCheckbox>
                        </CheckboxLayout>
                    )}
                </ModifierWrapper>
            </StyledButton>
        </Layout>
    );
};

const CheckboxLayout = styled.div`
    user-select: none;
    font-size: 20px;
    line-height: 24px;
    color: var(--color_ui_5);
`;

const StyledCheckbox = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
    width: 24px;
    height: 24px;
    font-size: 24px;
    line-height: 24px;

    .form-check {
        display: none;
    }
`;

const RadioLayout = styled.div`
    user-select: none;
    font-size: 20px;
    line-height: 24px;
    color: var(--color_ui_5);
`;

const StyledRadio = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
    width: 24px;
    height: 24px;
    font-size: 24px;
    line-height: 24px;

    .form-check {
        display: none;
    }
`;

const StyledButton = styled.div<{ modifier: Modifier }>`
    border: 0;
    text-align: left;
    display: flex;
    align-items: center;
    width: 100%;
    border-radius: var(--radius_l);
    padding: 0;

    &:focus {
        outline: none;
        box-shadow: none;
    }
`;

const ModifierWrapper = styled.div`
    cursor: pointer;
    display: inline-flex;
    align-items: start;
    padding: 0px;
    margin-bottom: 0;
    width: 100%;

    .mod-details {
        flex-grow: 1;
        overflow: hidden;
    }

    .mod-name {
        user-select: none;
        color: var(--text-black);
        white-space: nowrap;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 16px;
        line-height: 24px;

        .mod-name-text {
            text-overflow: ellipsis;
            overflow: hidden;
        }
    }

    .mod-description {
        user-select: none;
        font-size: 14px;
        line-height: 20px;
        font-weight: 400;
        color: var(--text-grey);
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
        min-width: 0px;
        display: block;
    }

    .mod-price {
        user-select: none;
        font-size: 16px;
        line-height: 24px;
        color: var(--text-grey);
        font-weight: 400;
        min-width: 60px;
        text-align: right;
        white-space: nowrap;
        flex: 1 0 auto;
        display: inline-block;
    }
`;

const QuantityWrapper = styled.div`
    user-select: none;
    margin-left: 4px;
    display: flex;
    align-items: center;
    gap: 4px;

    span {
        font-size: 16px;
        line-height: 20px;
        display: inline-flex;
        text-align: center;
        justify-content: center;
        color: var(--text-black);

        &.material-symbols-outlined {
            font-size: 24px;
            line-height: 24px;
            color: var(--color_ui_5);
        }
    }

    button {
        padding: 0;
        border: none;
        margin: 0;
        font: inherit;
        color: inherit;
        background-color: transparent;
        text-align: inherit;
        cursor: pointer;
        height: 24px;
        &:focus {
            outline: none;
        }
    }
`;

const Layout = styled.div<{ lastCheckbox: boolean }>`
    ${(props) => !props.lastCheckbox ? 'padding-bottom: 16px;' : ''}

    .badge--modifier {
        margin-right: 0 !important;
    }
`;

export default ModifierCheckbox;
